/**
 * Account step — PURE flow mappings (PR-ONB-RESUME-1B, locks #1/#2).
 *
 * The AccountPage delegates every decision to these functions so the
 * signup→bind state machine is unit-testable without Firebase or fetch:
 *
 *   - Firebase signup error codes → UI action. The pivotal case is
 *     `auth/email-already-in-use` (lock #2: signup succeeded on a previous
 *     attempt but the bind never completed, OR the email simply has an
 *     account): the UI switches to "Accedi per continuare" and, after a
 *     successful login, calls the IDEMPOTENT bind again. No account
 *     re-creation, no automatic password reset.
 *   - Bind (register) failures → UI message.
 *   - Draft payload extraction: the wizard form values minus the volatile
 *     anti-abuse fields. The password NEVER appears here — it lives in
 *     component-local state and goes straight to the Firebase SDK.
 */

import type { AreaDraftState, AreaFailure } from '@/lib/areaApi';

// ─── Firebase signup/login error mapping ─────────────────────────────────────

export type SignupErrorAction =
  | { action: 'SWITCH_TO_LOGIN'; message: string }
  | { action: 'FIELD_ERROR'; field: 'email' | 'password'; message: string }
  | { action: 'GENERIC'; message: string };

export function mapFirebaseSignupError(code: string): SignupErrorAction {
  switch (code) {
    case 'auth/email-already-in-use':
      return {
        action: 'SWITCH_TO_LOGIN',
        message:
          'Esiste già un account con questa email. Accedi per continuare la tua richiesta.',
      };
    case 'auth/invalid-email':
      return { action: 'FIELD_ERROR', field: 'email', message: 'Email non valida.' };
    case 'auth/weak-password':
    case 'auth/password-does-not-meet-requirements':
      return {
        action: 'FIELD_ERROR',
        field: 'password',
        message: 'Password troppo debole: usa almeno 8 caratteri.',
      };
    default:
      return {
        action: 'GENERIC',
        message: 'Creazione account non riuscita. Riprova tra qualche istante.',
      };
  }
}

export function mapFirebaseLoginError(code: string): string {
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Credenziali non valide. Controlla email e password.';
    case 'auth/too-many-requests':
      return 'Troppi tentativi. Attendi qualche minuto e riprova.';
    case 'auth/user-disabled':
      return 'Questo account non è abilitato. Contatta l\'assistenza.';
    default:
      return 'Accesso non riuscito. Riprova tra qualche istante.';
  }
}

// ─── Bind failure mapping ────────────────────────────────────────────────────

export function bindFailureMessage(failure: AreaFailure): string {
  switch (failure.kind) {
    case 'TURNSTILE_INVALID':
      return 'Verifica anti-abuso scaduta. Completa di nuovo il controllo e riprova.';
    case 'EMAIL_ALREADY_IN_USE':
      return 'Esiste già un account operativo con questa email. Accedi al portale oppure contatta l\'assistenza.';
    case 'ACCOUNT_EXISTS':
      return 'Hai già una richiesta in corso con questa email. Accedi per continuarla.';
    case 'ACCOUNT_CONVERTED':
      return 'Il tuo account è già stato attivato. Accedi al portale intermediari.';
    case 'ACCOUNT_DISABLED':
      return 'Questo account non è abilitato. Contatta l\'assistenza.';
    case 'COOLDOWN_ACTIVE':
      return `La tua precedente richiesta è stata respinta. Potrai presentare una nuova richiesta a partire dal ${formatCooldownDate(failure.cooldownUntil)}.`;
    case 'RESUMABLE_DISABLED':
      return 'L\'area onboarding non è al momento disponibile. Riprova più tardi.';
    case 'RATE_LIMITED':
      return 'Troppe richieste. Attendi un minuto e riprova.';
    case 'NETWORK':
      return 'Connessione assente. Controlla la rete e riprova.';
    default:
      return 'Operazione non riuscita. Riprova tra qualche istante.';
  }
}

export function formatCooldownDate(iso: string | undefined): string {
  if (!iso) return 'data indicata nella comunicazione ricevuta';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'data indicata nella comunicazione ricevuta';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${date.getFullYear()}`;
}

// ─── Draft payload / state builders ──────────────────────────────────────────

/**
 * Wizard form values → server draft payload. Strips the volatile anti-abuse
 * fields (the Turnstile token is one-shot; acceptance belongs to submit).
 * NOTE: the form NEVER holds the password (component-local state by design),
 * so no credential can leak into the draft — the unit test asserts it.
 */
export function buildDraftPayloadFromForm(
  values: Record<string, unknown>,
): Record<string, unknown> {
  const { turnstileToken: _t, acceptanceConfirmed: _a, ...rest } = values;
  return rest;
}

/** Wizard step keys in order — drives completedSteps/currentStep. */
export const WIZARD_STEP_ORDER = [
  'organizzazione',
  'contatti',
  'indirizzi',
  'amministratore',
  'account',
  'fiscale',
  'riepilogo',
] as const;
export type WizardStepKey = (typeof WIZARD_STEP_ORDER)[number];

/**
 * Draft state while the user is STILL ON `step` (review fix #2): the step
 * being edited is NOT completed — only its predecessors are. This is what
 * the AUTOSAVE persists: saving mid-page must never mark the page done nor
 * point the resume at the next one.
 */
export function draftStateForCurrentStep(step: WizardStepKey): AreaDraftState {
  const idx = WIZARD_STEP_ORDER.indexOf(step);
  return {
    currentStep: step,
    completedSteps: WIZARD_STEP_ORDER.slice(0, idx) as unknown as string[],
  };
}

/**
 * Draft state after `step` has been EXPLICITLY completed (validation passed
 * + the user clicked Continua / the bind succeeded): the step joins
 * completedSteps and the resume pointer advances to the next one. Never
 * call this from the autosave path.
 */
export function draftStateAfterCompletedStep(step: WizardStepKey): AreaDraftState {
  const idx = WIZARD_STEP_ORDER.indexOf(step);
  return {
    currentStep: WIZARD_STEP_ORDER[idx + 1] ?? step,
    completedSteps: WIZARD_STEP_ORDER.slice(0, idx + 1) as unknown as string[],
  };
}

/** Resume target: a stored currentStep → wizard URL (defensive fallback). */
export function resumeStepPath(currentStep: string | null | undefined): string {
  const step = (WIZARD_STEP_ORDER as readonly string[]).includes(currentStep ?? '')
    ? (currentStep as WizardStepKey)
    : 'organizzazione';
  return `/registrazione-intermediario/${step}`;
}
